import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import instance from '../../lib/api/api'

type Resources = {
  ram_used_gb: number
  ram_total_gb: number
  ram_percent: number
  cpu_percent: number
}

const REFRESH_MS = 2000

function colorForLoad(pct: number): string {
  if (pct >= 90) return 'bg-red-500'
  if (pct >= 75) return 'bg-yellow-400'
  return 'bg-emerald-500'
}

function textColorForLoad(pct: number): string {
  if (pct >= 90) return 'text-red-600 dark:text-red-400'
  if (pct >= 75) return 'text-yellow-600 dark:text-yellow-500'
  return 'text-gray-600 dark:text-gray-300'
}

export default function ResourceMonitor() {
  const { t } = useTranslation()
  const [data, setData] = useState<Resources | null>(null)
  const [failed, setFailed] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    let timer: number | null = null

    const fetchOnce = async () => {
      try {
        const resp = await instance.get<Resources>('/api/v1/system/resources')
        if (!mountedRef.current) return
        setData(resp.data)
        setFailed(false)
      } catch {
        if (!mountedRef.current) return
        setFailed(true)
      }
    }

    fetchOnce()
    timer = window.setInterval(fetchOnce, REFRESH_MS)

    return () => {
      mountedRef.current = false
      if (timer != null) window.clearInterval(timer)
    }
  }, [])

  if (failed) {
    return (
      <div
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs text-gray-400"
        title={t('resourceMonitor.unavailable')}
      >
        <span>—</span>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs text-gray-400">
        <span className="tabular-nums">{t('resourceMonitor.ramLoading')}</span>
        <span className="tabular-nums">{t('resourceMonitor.cpuLoading')}</span>
      </div>
    )
  }

  const ramPct = Math.min(100, Math.max(0, data.ram_percent))
  const cpuPct = Math.min(100, Math.max(0, data.cpu_percent))

  return (
    <div
      className="hidden md:flex items-center gap-3 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
      title={t('resourceMonitor.tooltip', {
        used: data.ram_used_gb.toFixed(1), total: data.ram_total_gb.toFixed(1),
        ramPct: ramPct.toFixed(0), cpuPct: cpuPct.toFixed(0)
      })}
    >
      {/* RAM */}
      <div className="flex items-center gap-1.5">
        <span className={`text-[11px] font-medium ${textColorForLoad(ramPct)} tabular-nums`}>
          {t('resourceMonitor.ram')}
        </span>
        <span className="text-xs text-gray-700 dark:text-gray-200 tabular-nums">
          {data.ram_used_gb.toFixed(0)}/{data.ram_total_gb.toFixed(0)}
        </span>
        <div className="w-16 h-1.5 rounded bg-gray-300 dark:bg-gray-600 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ease-out ${colorForLoad(ramPct)}`}
            style={{ width: `${ramPct}%` }}
          />
        </div>
      </div>

      {/* CPU */}
      <div className="flex items-center gap-1.5">
        <span className={`text-[11px] font-medium ${textColorForLoad(cpuPct)} tabular-nums`}>
          {t('resourceMonitor.cpu')}
        </span>
        <span className="text-xs text-gray-700 dark:text-gray-200 tabular-nums w-8 text-right">
          {cpuPct.toFixed(0)}%
        </span>
      </div>
    </div>
  )
}
